/**
 * 콘텐츠 와이어 타입(클라/엣지/관리자 공유 단일 정의) — CLAUDE.md 코딩규칙.
 * 게시 매니페스트·레이아웃 좌표 등 출처를 넘나드는 JSON 형태를 한 곳에서 정의.
 */

import type { ArInteract } from "./arInteract";

/** 게시 에셋 1개(슬롯키 → 이미지 URL). pos/scale 은 위치 지정 슬롯(랜딩 캐릭터·배경 등)에서 사용. */
export interface AssetManifestEntry {
  url: string;
  alt?: string;
  pos?: { x: number; y: number }; // 화면 내 상대 위치(0~1). 미설정 시 기본 배치.
  /**
   * **좁은 화면(폰)에서만** 쓸 자리. 관리자 [🏠 홈 화면]의 「📱 좁은 화면」 탭에서 드래그로 정한다.
   *
   * 자리는 무대 폭의 비율이라 화면이 좁아져도 상대 위치가 그대로다 — 넓은 화면에서 왼쪽에
   * 나란히 둔 캐릭터가 폰에서는 **왼쪽에 겹쳐 몰리고 오른쪽이 텅 빈다.**
   * 안 정해 두면 `stagePosForWidth` 의 기본 규칙(병아리를 히어로 오른쪽으로)이 대신한다.
   */
  posNarrow?: { x: number; y: number };
  scale?: number; // 크기 배율(기본 1)
  z?: number; // 앞뒤 순서(CSS zIndex). 겹치는 랜딩 캐릭터 우선순위. 미설정=기본 배치순서.
  fps?: number; // 프레임 애니 스트립(가로 정사각 N프레임) 재생 속도. 랜딩 루프 ≤3(시각안전), 걷기 ~8.
  /** 스트립을 한 번만 재생하고 마지막 장에 멈춘다(빵 한 입). 재생 규칙은 web `stripStyle` 한 곳 */
  once?: boolean;
  motion?: string; // AR 오버레이 이동 프리셋(none|float|walk|rise) — 페이지 위에서 둥실/가로질러/솟아오름
  fallbackYoutube?: string; // AR 타깃 엔트리 전용 — 인식 안 될 때 "영상으로 보기"(FR-AR-04) 유튜브
  /** 🧊 AR 보기 「④ 3D 모델」 전용 — 남의 자료(CC BY)면 **의무**인 출처 표기. 화면 아래에 붙는다(shared `arModelMagic`). */
  credit_ko?: string;
  /**
   * 📐 **카메라 기울기** — `ar.tilt` 한 자리에만 산다(장면별이 아니라 서비스 하나의 설정).
   * 새 표·새 API 를 안 만든다. 켜고 끄는 것은 관리자 [📐 카메라 기울기], 판정은 shared `arTilt`.
   * 🔴 기본은 **꺼짐** — 카메라가 있어야 확인되는 기능이라 폰에서 보고 켠다.
   */
  on?: boolean;
  /** 📐 기울기를 얼마나 따라갈까(0~1). 손 떨림이 그대로 나오면 원근보다 나쁘다. */
  strength?: number;
  /**
   * 🪄 **「누르면 일어나는 마법」의 정의** — AR 타깃 엔트리 전용(docs/build/35, FR-AR-09).
   * 층·누르는 자리·손잡이. `fallbackYoutube` 와 같은 자리에 산다 — 새 표·새 API 없이,
   * 정적 폴백 매니페스트에도 함께 실려 **오프라인에서도 평면 모드가 산다**(불변식 3).
   * 외부 입력이다 — 화면은 반드시 `normalizeArInteract` 를 지난 것만 쓴다(불변식 7).
   */
  interact?: ArInteract;
  prev?: string; // 직전 게시 URL(1단계 롤백용). 해시 파일명이라 이전 파일은 R2에 그대로 남는다.
  /**
   * 🎬 **이 걷기 그림을 어느 정지 그림에서 구웠나**(매니페스트 키). 손으로 올린 것에는 없다.
   *
   * 이 한 줄이 있어야 「다시 구울지」를 판단할 수 있다 — 없으면 둘 중 하나를 포기해야 한다:
   * 새로 넣은 옆모습이 조용히 무시되거나(예전 동작), **손으로 그려 올린 걷기가 덮어써지거나.**
   * 판정은 `walkBakePlan`(shared) 한 곳에 있다.
   */
  bakedFrom?: string;
  /**
   * 🎙️ **이 읽어주기 소리를 어떤 글로 만들었나** — 읽어주기(`book.audio.*`) 항목 전용.
   *
   * 자막을 고쳐도 소리는 옛 글 그대로 남는다(「이미 있는 것은 다시 안 만든다」). 이 한 줄이 있어야
   * 「자막과 소리가 갈렸다」를 알 수 있다 — 판정은 shared `dubRows`(`stale`). 없으면 모르는 것으로 둔다.
   */
  fromText?: string;
  /**
   * 🗑️ **여기는 비워 둔다** — 게시본에만 쓰는 표시.
   *
   * 웹은 정적 기본 매니페스트 **위에** 게시본을 덮는다(`{...정적, ...게시본}`).
   * 그래서 게시본에서 키를 **빼면** 정적 그림이 되살아난다 — 관리자에선 지웠는데
   * 아이 화면은 그대로다. 이 저장소가 경고해 온 「조용히 어긋남」이 딱 이 모양이다.
   *
   * 키를 빼는 대신 `hidden: true` 를 **남겨서** 「지웠다」를 기록한다.
   * 합치는 쪽(`mergeAssetManifests`)이 그 키를 떨어뜨린다.
   */
  hidden?: boolean;
  /**
   * 🙈 **웹에서 내림** — 올린 그림을 **지우지 않고** 아이 화면에서만 뺀다.
   *
   * 지우기와 다르다. 지우면 그림이 사라져서 되돌리려면 다시 올려야 한다(직접 만든 것은
   * 다시 만들어야 한다). 「이 캐릭터는 이번 행사 끝나면 내리자」·「이 굿즈는 품절이라
   * 잠깐 빼자」 같은 일에 지우기를 쓰게 하면 안 된다.
   *
   * 🔴 `hidden`(지움 표시)과 **다른 자리에 둔다.** 둘을 한 값으로 쓰면 「지운 것」과
   * 「내린 것」을 구분할 수 없고, 관리자에서 되돌릴 목록을 만들 수가 없다.
   * 지움은 `url` 이 비어 있고, 내림은 **`url` 이 그대로 남아 있다** — 그래서 되돌아온다.
   *
   * 아이 화면에서는 둘 다 똑같이 없다(`mergeAssetManifests`).
   */
  offWeb?: boolean;
}

/** 게시 에셋 매니페스트(슬롯키 → 엔트리). 운영자 게시 결과. */
export interface AssetManifest {
  version: number;
  updatedAt?: string;
  assets: Record<string, AssetManifestEntry>;
}

/**
 * 정적 기본 + 게시본을 합친다 — **웹과 관리자가 같은 함수를 쓴다.**
 *
 * 두 가지를 지킨다:
 *  ① 게시본에 없는 키는 정적 그림이 그대로 보인다(기본 아트는 게시가 실패해도 보장된다)
 *  ② 게시본이 `hidden` 으로 표시한 키는 **사라진다** — 운영자가 지운 건 지워져야 한다
 *
 * ②가 없던 동안, 기본 제공 그림은 관리자에서 아무리 지워도 아이 화면에 그대로 남았다.
 */
export function mergeAssetManifests(
  base: Record<string, AssetManifestEntry>,
  over: Record<string, AssetManifestEntry>,
  opts?: {
    /**
     * 🙈 **관리자만 켠다.** 「웹에서 내린」 것을 결과에 남긴다 — 안 남기면 관리자 화면에서도
     * 사라져서 **다시 올릴 방법이 없어진다.** 아이 화면은 절대 켜지 않는다.
     *
     * 지움 표시(`hidden`)는 여기서도 안 남긴다 — 그건 그림이 없는 자리라 되돌릴 것도 없다.
     */
    keepOffWeb?: boolean;
  },
): Record<string, AssetManifestEntry> {
  const out: Record<string, AssetManifestEntry> = { ...base, ...over };
  for (const [k, e] of Object.entries(out)) {
    if (e?.hidden) delete out[k];
    else if (e?.offWeb && !opts?.keepOffWeb) delete out[k];
  }
  return out;
}

/** 이 자리는 지금 아이 화면에 나가고 있나. */
export function isOnWeb(e: AssetManifestEntry | undefined): boolean {
  return !!e?.url && !e.hidden && !e.offWeb;
}

/**
 * 🔴 **자리가 있는 것과 그림이 있는 것은 다르다** — 여러 자리 중 **진짜 나가는 첫 자리**.
 *
 * 관리자에서 [준비 지우기]를 누르면 자리는 남고 주소만 빈다(`{ url: "" }`) — 되살릴 수
 * 있게 일부러 그렇게 둔다. `hidden` 이 아니라서 합치기에서도 안 떨어진다.
 * 운영에 실제로 그런 자리가 있다(`ar.target.ar_chumseongdae`).
 *
 * 그런데 폴백을 `A ?? B` 로 고르면 **빈 A 가 멀쩡한 B 를 가린다** — A 는 객체라 참이다.
 * 그래서 「전용 표지를 지웠더니 표지가 통째로 사라진다」가 된다(표지 페이지 그림이
 * 멀쩡히 있는데도). 고르는 일을 여기 한 곳에 둔다.
 */
export function firstOnWeb(
  ...entries: (AssetManifestEntry | undefined)[]
): AssetManifestEntry | undefined {
  return entries.find((e) => isOnWeb(e));
}

/** 상대 좌표(0~1). 전자책 레이아웃(자막·모션텍스트) 위치 등. */
export interface LayoutPos {
  x: number;
  y: number;
}
